import * as React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import PropTypes from 'prop-types'
import { withStyles } from '@mui/styles'
import { bindActionCreators } from 'redux'
//core files
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import CloudUpload from '@mui/icons-material/CloudUpload'
import Send from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ImageIcon from '@mui/icons-material/Image'
import Dropzone from 'react-dropzone'

import ModalDialog from '../ModalDialog/ModalDialog'
import { showNotification } from '../../store/notification/actionCreator'
import apiConfig from '../../config/apiConfig'
import { praxis } from '../../config/themeConfig'

import {
  TEXT_ERROR_SUPPORT_FORM,
  TEXT_ERROR_SUPPORT_FORM_ATTACHMENT,
  TEXT_SUCCESS_SUPPORT_FORM,
} from '../../constants/notifications'

const styles = {
  largeIcon: {
    width: '60px',
    height: '60px',
    color: praxis.lighterBlue,
  },
  customButton: {
    marginTop: '10px',
    backgroundColor: '#188295',
    color: 'white',
    borderRadius: '8px',
    padding: '12px 24px',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#0f6b7d',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(24, 130, 149, 0.3)',
    },
    transition: 'all 0.2s ease',
  },
  root: {
    textAlign: 'center',
    width: '100%',
  },
  wrapper: {
    position: 'relative',
  },
  buttonSuccess: {
    background: 'linear-gradient(135deg, #188295 0%, #1a6ec5 100%)',
    color: 'white',
    borderRadius: '8px',
    padding: '12px 32px',
    fontWeight: 600,
    fontSize: '1rem',
    boxShadow: '0 4px 15px rgba(24, 130, 149, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f6b7d 0%, #1565c0 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 20px rgba(24, 130, 149, 0.4)',
    },
    transition: 'all 0.2s ease',
  },
  buttonProgress: {
    color: '#188295',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  container: {
    marginTop: '20px',
    padding: '0 8px',
    '&>*': {
      marginTop: '0px !important',
      marginBottom: '24px !important',
    },
  },
  errorMessage: {
    fontSize: '12px',
    margin: '8px',
    color: '#f44336',
    fontWeight: 500,
  },
  textField: {
    width: '100%',
    margin: '8px 0',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fafafa',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
      '&.Mui-focused': {
        backgroundColor: 'white',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: '#666',
    },
  },
  subTitle: {
    fontSize: '16px',
    color: '#666',
    fontStyle: 'italic',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0f7f7',
    marginBottom: '20px',
  },
  dropArea: {
    minWidth: '520px',
    position: 'relative',
    height: '160px',
    border: '2px dashed #188295',
    borderRadius: '16px',
    margin: '16px',
    padding: '20px',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0fdfb',
      borderColor: '#0f6b7d',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(24, 130, 149, 0.1)',
    },
  },
  typographyBody1: {
    color: '#333',
    fontWeight: 500,
  },
  formControl: {
    marginBottom: '24px',
    '& .MuiFormLabel-root': {
      fontWeight: 600,
      fontSize: '1.1rem',
      color: '#333',
      marginBottom: '12px',
    },
  },
  // Base form styling
  modernCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    border: '1px solid #f3f4f6',
    padding: '24px',
    margin: '8px 0',
    transition: 'all 0.2s ease',
  },
  modernTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fafbfc',
      fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#e5e7eb',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#188295',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#188295',
        borderWidth: '2px',
        boxShadow: '0 0 0 3px rgba(24, 130, 149, 0.1)',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 500,
      color: '#6b7280',
      '&.Mui-focused': {
        color: '#188295',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.8rem',
      color: '#9ca3af',
      marginTop: '6px',
    },
  },
  modernSection: {
    marginBottom: '32px',
    '& .MuiFormLabel-root': {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '16px',
      letterSpacing: '0.025em',
    },
  },
  radioGroup: {
    '& .MuiFormGroup-root': {
      gap: '6px',
    },
  },
  formControlLabel: {
    margin: '3px 0',
    padding: '0px 16px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: '50px',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#f0fdfb',
      borderColor: '#188295',
      boxShadow: '0 4px 12px rgba(24, 130, 149, 0.15)',
      transform: 'translateY(-1px)',
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '0.95rem',
      fontWeight: 500,
      color: '#374151',
      lineHeight: '1.5',
    },
    '& .MuiRadio-root': {
      padding: '8px',
      color: '#9ca3af',
      '&.Mui-checked': {
        color: '#188295',
      },
      '&:hover': {
        backgroundColor: 'rgba(24, 130, 149, 0.1)',
      },
    },
  },
  formSection: {
    marginBottom: '24px',
    '& .MuiFormLabel-root': {
      fontSize: '1rem',
      fontWeight: 400,
      color: '#555',
      marginBottom: '12px',
      letterSpacing: '0.5px',
    },
  },
  compactFormControl: {
    width: '100%',
    marginBottom: '20px',
    '& .MuiFormLabel-root': {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#555',
      marginBottom: '8px',
    },
  },
  radioOption: {
    margin: '8px 0',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#e3f2fd',
      borderColor: '#bbdefb',
    },
    '&.selected': {
      backgroundColor: '#e8f5e8',
      borderColor: '#4caf50',
      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
    },
  },
  assetTypeCard: {
    padding: '16px',
    marginBottom: '12px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      borderColor: '#9ca3af',
      backgroundColor: '#f9fafb',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
    '&.selected': {
      borderColor: '#188295',
      backgroundColor: '#f0fdfb',
      boxShadow: '0 4px 12px rgba(24, 130, 149, 0.2)',
      '&:hover': {
        borderColor: '#188295',
        backgroundColor: '#e0faf7',
        boxShadow: '0 6px 16px rgba(24, 130, 149, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '12px',
        right: '12px',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #188295 0%, #0f6b7d 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        boxShadow: '0 2px 8px rgba(24, 130, 149, 0.3)',
      },
      '&::after': {
        content: '"✓"',
        position: 'absolute',
        top: '16px',
        right: '16px',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        color: 'white',
        fontSize: '18px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      },
    },
  },
  chipContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px',
    minHeight: '80px',
    backgroundColor: '#f9fafb',
    transition: 'all 0.2s ease',
    '&.focused': {
      borderColor: '#9ca3af',
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    '&:hover': {
      borderColor: '#d1d5db',
      backgroundColor: '#f3f4f6',
    },
  },
  chipInput: {
    border: 'none',
    outline: 'none',
    fontSize: '0.9rem',
    padding: '6px 8px',
    width: '100%',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    color: '#374151',
    '&::placeholder': {
      color: '#9ca3af',
      fontStyle: 'normal',
      fontSize: '0.85rem',
    },
  },
  assetChip: {
    margin: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    },
    '&.dam': {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #9ca3af',
      '&:hover': {
        backgroundColor: '#e5e7eb',
        borderColor: '#6b7280',
      },
    },
    '&.scene7': {
      backgroundColor: '#f0fdfb',
      color: '#0f6b7d',
      border: '1px solid #188295',
      '&:hover': {
        backgroundColor: '#e0faf7',
        borderColor: '#0f6b7d',
      },
    },
  },
  sectionTitle: {
    fontWeight: 500,
    fontSize: '1rem',
    color: '#374151',
    marginBottom: '12px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '6px',
    letterSpacing: '0.3px',
  },
  assetCounter: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '0.85rem',
    fontWeight: 500,
    display: 'inline-block',
    margin: '8px 0',
    border: '1px solid #e5e7eb',
  },
  assetInputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fafafa',
      fontSize: '14px',
      '& fieldset': {
        borderColor: '#e0e0e0',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: '#2196f3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      fontWeight: 500,
    },
  },
  assetSummaryText: {
    marginTop: '8px',
    marginBottom: '20px',
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 400,
    color: '#4b5563',
    border: '1px solid #e5e7eb',
  },
  errorText: {
    marginBottom: '10px',
    padding: '8px 12px',
    backgroundColor: '#ffebee',
    borderRadius: '8px',
    border: '1px solid #f8bbd9',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #188295 0%, #4c6e91 100%)',
    borderRadius: '8px',
    border: 0,
    color: 'white',
    height: 44,
    padding: '0 28px',
    boxShadow: '0 3px 12px rgba(24, 130, 149, 0.3)',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f6b7d 0%, #1565c0 100%)',
      boxShadow: '0 4px 16px rgba(24, 130, 149, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
      boxShadow: '0 2px 8px rgba(24, 130, 149, 0.3)',
    },
    '&:disabled': {
      background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
      boxShadow: 'none',
      transform: 'none',
      color: '#9ca3af',
    },
    marginTop: 0,
  },
  restoreSection: {
    marginTop: '16px',
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    paddingBottom: '0px',
  },
  assetTypeCardContent: {
    '& .MuiTypography-subtitle1': {
      color: '#374151',
      fontWeight: 500,
      fontSize: '1rem',
      marginBottom: '4px',
    },
    '& .MuiTypography-body2': {
      color: '#6b7280',
      fontSize: '0.85rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },
  uploadSection: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  dropAreaEnhanced: {
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    padding: '32px 24px',
    textAlign: 'center',
    backgroundColor: '#fafbfc',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      borderColor: '#188295',
      backgroundColor: '#f0fdfb',
      boxShadow: '0 4px 15px rgba(24, 130, 149, 0.1)',
      transform: 'translateY(-1px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'linear-gradient(45deg, transparent 30%, rgba(24, 130, 149, 0.05) 50%, transparent 70%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover::before': {
      opacity: 1,
    },
  },
  uploadText: {
    color: '#6b7280',
    fontSize: '0.95rem',
    marginTop: '12px',
    marginBottom: '16px',
    fontWeight: 400,
  },
  browseButton: {
    background: 'linear-gradient(135deg, #188295 0%, #4c6e91 100%)',
    color: 'white',
    borderRadius: '6px',
    padding: '8px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 6px rgba(24, 130, 149, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f6b7d 0%, #1565c0 100%)',
      boxShadow: '0 3px 10px rgba(24, 130, 149, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successIcon: {
    fontSize: '80px',
    color: '#188295',
    marginBottom: '20px',
    filter: 'drop-shadow(0 4px 8px rgba(24, 130, 149, 0.3))',
  },
  successTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '12px',
  },
  successMessage: {
    fontSize: '1rem',
    color: '#6b7280',
    marginBottom: '20px',
    lineHeight: '1.6',
    maxWidth: '500px',
    whiteSpace: 'pre-line',
  },
  jiraInfo: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    maxWidth: '400px',
    width: '100%',
  },
  jiraLabel: {
    fontSize: '0.85rem',
    color: '#374151',
    fontWeight: 500,
    marginBottom: '4px',
  },
  jiraId: {
    fontSize: '1.1rem',
    color: '#4c6e91',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  successButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  closeButton: {
    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
    color: '#374151',
    borderRadius: '8px',
    padding: '12px 28px',
    fontSize: '0.9rem',
    fontWeight: 600,
    border: '1px solid #d1d5db',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
      borderColor: '#9ca3af',
      transform: 'translateY(-1px)',
      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #188295 0%, #4c6e91 100%)',
    color: 'white',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '0.9rem',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(24, 130, 149, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f6b7d 0%, #1565c0 100%)',
      boxShadow: '0 3px 12px rgba(24, 130, 149, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
}

const FEEDBACK_TEXTS = {
  // Form Labels
  issueType: 'Issue Type',
  responsePriority: 'Response Priority',
  detailedDescription: 'Detailed Description',
  attachments: 'Attachments',
  selectAssetType: 'Select Asset Type',

  // Issue Type Options
  toolIssue: 'I am experiencing a tool issue',
  toolQuestion: 'I have a tool or process question',
  assetLocation: 'I cannot locate an asset',
  assetRestore: 'I need asset restore or republish',

  // Placeholder Text
  descriptionPlaceholder: 'Describe your issue or request in detail...',
  descriptionPlaceholderOptional:
    'Add any additional notes or context (optional)...',
  attachmentsPlaceholder: 'Drag and drop files here or click to browse',

  // Validation Messages
  issueTypeRequired: 'Please select an issue type',
  priorityRequired: 'Please select a priority',
  descriptionRequired: 'Please provide a description',

  // Helper Text
  issueTypeHelper: 'Select the category that best describes your request',
  priorityHelper: 'Choose your preferred response timeframe',
  descriptionHelper: 'Please provide details about your request',
  descriptionHelperOptional: 'Additional details (optional)',
  attachmentsHelper:
    'You can attach screenshots, logs, or other relevant files (max 10MB each)',

  // Button Text
  submit: 'Submit Request',
  cancel: 'Cancel',

  // Success Messages
  submitSuccess: 'Request Submitted Successfully!',
  operationsMessage:
    'request submitted successfully!\nOur operations team will process your request shortly.',

  optional: '(Optional)',
  required: '*',
}

const HighPriority = [
  {
    value: 'High Priority',
    label: 'High Priority',
  },
  {
    value: 'Medium Priority',
    label: 'Medium Priority',
  },
  {
    value: 'Low Priority',
    label: 'Low Priority',
  },
]

export class Feedback extends React.Component {
  static defaultProps = {}

  constructor(props) {
    super(props)
    this.onModalClose = this.onModalClose.bind(this)
    this.formSubmit = this.formSubmit.bind(this)
    this.state = {
      files: [],
      modalOpen: true,
      open: false,
      vertical: null,
      horizontal: null,
      selectedValue: FEEDBACK_TEXTS.toolIssue,
      contactTeam: 'AssetHub Product',
      priority: 'High Priority',
      description: '',
      descLength: 0,
      loading: false,
      assetType: '',
      assets: [],
      currentInput: '',
      inputFocused: false,
      duplicateAttempt: '',
      showSuccess: false,
      successMessage: '',
      successJiraId: '',
    }
  }

  handleSuccessClose = () => {
    this.setState({
      showSuccess: false,
      successMessage: '',
      successJiraId: '',
    })
    this.onModalClose()
  }

  handleNewRequest = () => {
    this.setState({
      showSuccess: false,
      successMessage: '',
      successJiraId: '',
      files: [],
      selectedValue: FEEDBACK_TEXTS.toolIssue,
      contactTeam: 'AssetHub Product',
      priority: 'High Priority',
      description: '',
      descLength: 0,
      assetType: '',
      assets: [],
      currentInput: '',
      inputFocused: false,
      duplicateAttempt: '',
    })
  }

  onDrop(files) {
    this.setState({
      files,
    })
  }
  showSnackBar(message, variant) {
    this.props.showNotification(true, message, variant)
    this.setState({
      loading: false,
    })
  }

  formSubmit = () => {
    const { user = {} } = this.props
    const { email = '' } = user
    const { key = '', assethub = {} } = apiConfig
    const { feedBackUrl = '' } = assethub
    const projectKey = 'ASSETHUB'
    const {
      description = '',
      priority = '',
      selectedValue = '',
      files = [],
      assetType = '',
      assets = [],
    } = this.state

    let finalDescription = description
    let issueType = 'Defect'

    if (selectedValue === 'I need asset restore or republish') {
      issueType = 'Task'
      const assetsList = assets.join(', ')
      const requestType =
        assetType === 'DAM' ? 'Asset Restore' : 'Asset Republish'
      finalDescription = `
      Asset Type: ${assetType}
      Request Type: ${requestType}
      Assets: ${assetsList}
      Additional Notes: ${description || 'None'}
      Requested by: ${email}
      `.trim()
    }

    const reqData = {
      email: email,
      issuetype: issueType,
      projectkey: projectKey,
      description: finalDescription,
      summary: priority + ' | ' + selectedValue,
    }
    this.setState({
      loading: true,
    })
    axios
      .post(feedBackUrl + '?key=' + key, reqData)
      .then((response) => {
        const { data: fbuRespData = {} } = response
        const { issue_id: issueId = '' } = fbuRespData

        let successMessage = TEXT_SUCCESS_SUPPORT_FORM.replace(
          /<br\s*\/?>/gi,
          '\n',
        )
        if (selectedValue === FEEDBACK_TEXTS.assetRestore && issueId) {
          const requestType =
            assetType === 'DAM' ? 'Asset Restore' : 'Asset Republish'
          successMessage = `${requestType} ${FEEDBACK_TEXTS.operationsMessage}`
        }

        if (files.length > 0) {
          const url = feedBackUrl + '/attachment/' + issueId + '?key=' + key
          // populate image attachement to created JIRA
          const formData = new FormData()
          files.forEach(function (value) {
            formData.append('file', value)
          })
          formData.append('jiraId', issueId)
          axios
            .post(url, formData, {
              headers: {
                'Content-Type': undefined,
              },
            })
            .then(() => {
              this.setState({
                showSuccess: true,
                successMessage: successMessage,
                successJiraId: issueId,
                loading: false,
              })
            })
            .catch(() => {
              this.showSnackBar(TEXT_ERROR_SUPPORT_FORM_ATTACHMENT, 'error')
              this.onModalClose()
            })
        } else {
          this.setState({
            showSuccess: true,
            successMessage: successMessage,
            successJiraId: issueId,
            loading: false,
          })
        }
      })
      .catch(() => {
        this.showSnackBar(TEXT_ERROR_SUPPORT_FORM, 'error')
      })
  }
  handleRadioChange = (event) => {
    this.setState({ selectedValue: event.target.value })
    let contactTeam =
      event.target.value === FEEDBACK_TEXTS.toolIssue ||
      event.target.value === FEEDBACK_TEXTS.toolQuestion
        ? 'AssetHub Product'
        : event.target.value === FEEDBACK_TEXTS.assetRestore
          ? 'AssetHub Operations'
          : 'Assets'
    this.setState({ contactTeam: contactTeam })
  }
  handlePriorityChange = (event) => {
    this.setState({ priority: event.target.value })
  }
  onModalClose() {
    this.resetState()
    this.props.onClose()
  }
  resetState() {
    this.setState({
      modalOpen: true,
      showSuccess: false,
      successMessage: '',
      successJiraId: '',
    })
  }
  handleClick = (state) => () => {
    this.setState({ open: true, ...state })
  }

  handleClose = () => {
    this.setState({ open: false })
  }
  renderDialogActions() {}
  handleChange = (name) => (event) => {
    this.setState({
      [name]: event.target.value,
    })
  }
  handleDescriptionChange = (event) => {
    this.setState({
      description: event.target.value,
      descLength: event.target.value.length,
    })
  }

  // Asset management methods
  handleAssetTypeSelect = (type) => {
    this.setState({ assetType: type })
  }

  addAssetIDs = (assetValue) => {
    const trimmedValue = assetValue.trim()
    if (!trimmedValue) return false

    // Remove special characters, keep only alphanumeric, hyphens, underscores, dots, and forward slashes
    const cleanedValue = trimmedValue.replace(/[^a-zA-Z0-9\-_.:/]/g, '')
    if (!cleanedValue) return false

    // Check for duplicates (case-insensitive)
    const isDuplicate = this.state.assets.some(
      (asset) => asset.toLowerCase() === cleanedValue.toLowerCase(),
    )

    if (isDuplicate) {
      this.setState({ duplicateAttempt: cleanedValue })
      setTimeout(() => this.setState({ duplicateAttempt: '' }), 2000)
      return false
    }

    this.setState({
      assets: [...this.state.assets, cleanedValue],
    })
    return true
  }

  removeAsset = (assetToRemove) => {
    this.setState({
      assets: this.state.assets.filter((asset) => asset !== assetToRemove),
    })
  }

  handleAssetInputChange = (event) => {
    this.setState({ currentInput: event.target.value })
  }

  handleInputKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      if (this.state.currentInput.trim()) {
        this.addAssetIDs(this.state.currentInput)
        this.setState({ currentInput: '' })
      }
    }
  }

  handleInputKeyDown = (event) => {
    if (
      event.key === 'Backspace' &&
      !this.state.currentInput.trim() &&
      this.state.assets.length > 0
    ) {
      const lastAsset = this.state.assets[this.state.assets.length - 1]
      this.removeAsset(lastAsset)
    }
  }

  handleInputPaste = (event) => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    const assetsIDs = pastedText
      .split(/[\n,;]/)
      .map((item) => item.trim())
      .filter((item) => item)

    assetsIDs.forEach((asset) => this.addAssetIDs(asset))
    this.setState({ currentInput: '' })
  }

  getAssetChipClass = (asset) => {
    if (this.state.assetType === 'DAM') return 'dam'
    if (this.state.assetType === 'Scene7') return 'scene7'
    // Auto-detect if no type selected
    if (asset.includes('http') || asset.includes('scene7')) return 'scene7'
    return 'dam'
  }

  renderSuccessScreen() {
    const { classes } = this.props
    const { successMessage, successJiraId } = this.state

    return (
      <Box className={classes.successContainer}>
        <CheckCircle className={classes.successIcon} />
        <Typography className={classes.successTitle}>
          {FEEDBACK_TEXTS.submitSuccess}
        </Typography>
        <Typography className={classes.successMessage}>
          {successMessage}
        </Typography>

        <Box className={classes.successButtons}>
          <Button
            variant="contained"
            onClick={this.handleNewRequest}
            className={classes.primaryButton}
          >
            Submit Another Request
          </Button>
          <Button
            variant="contained"
            onClick={this.handleSuccessClose}
            className={classes.closeButton}
          >
            Close
          </Button>
        </Box>
      </Box>
    )
  }

  render() {
    const { classes = {}, user = {}, env = {} } = this.props
    const { FEATURE_CONFIG = {} } = env
    const isHelpFormEnabled = FEATURE_CONFIG.ENABLE_ASSETHUB_HELP_FORM !== false
    const { email = '' } = user
    const {
      selectedValue = '',
      contactTeam = '',
      currency,
      multiline,
      descLength = 0,
      loading = false,
      files = [],
      assetType = '',
      assets = [],
    } = this.state

    if (!isHelpFormEnabled) {
      return (
        <div {...this.props.styles1}>
          <ModalDialog
            title="Need Help?"
            open={this.props.modalOpen}
            onClose={() => this.onModalClose()}
            renderActions={this.renderDialogActions}
            className={classes.createModal}
            dialogClassName={classes.createModal}
          >
            <Box sx={{ padding: '8px 8px 4px' }}>
              <Typography
                component="div"
                sx={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#1f2937',
                  whiteSpace: 'pre-line',
                }}
              >
                The AssetHub Help Form is temporarily unavailable. For assistance,
                please use one of the following options:
                <br />
                <br />
                Internal Target users: Submit a support request in Slack at{' '}
                <a
                  href="https://target.enterprise.slack.com/archives/CF6JVHAQK"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2' }}
                >
                  #assethub-help
                </a>
                .
                <br />
                External users: Email{' '}
                <a
                  href="mailto:assethub@target.com"
                  style={{ color: '#1976d2' }}
                >
                  assethub@target.com
                </a>
                .
              </Typography>
            </Box>
          </ModalDialog>
        </div>
      )
    }

    // Show success screen if submission was successful
    if (this.state.showSuccess) {
      return (
        <div {...this.props.styles1}>
          <ModalDialog
            title="Request Submitted"
            open={this.props.modalOpen}
            onClose={() => this.handleSuccessClose()}
            renderActions={this.renderDialogActions}
            className={classes.createModal}
            dialogClassName={classes.createModal}
          >
            {this.renderSuccessScreen()}
          </ModalDialog>
        </div>
      )
    }

    return (
      <div {...this.props.styles1}>
        <ModalDialog
          title="Need Help?"
          open={this.props.modalOpen}
          onClose={() => this.onModalClose()}
          renderActions={this.renderDialogActions}
          className={classes.createModal}
          dialogClassName={classes.createModal}
        >
          <Grid container justifyContent="center" spacing={1}>
            <Grid item xs={12}>
              <form
                className={classes.container}
                noValidate
                autoComplete="off"
                id="feedbackForm"
              >
                <FormControl
                  variant="standard"
                  className={classes.textField}
                  disabled
                >
                  <InputLabel htmlFor="email-disabled">your email</InputLabel>
                  <Input
                    id="email-disabled"
                    value={email}
                    onChange={this.handleChange}
                  />
                </FormControl>
                <FormControl
                  variant="standard"
                  component="fieldset"
                  required
                  className={`${classes.modernTextField} ${classes.formSection}`}
                >
                  <FormLabel component="legend">
                    {FEEDBACK_TEXTS.issueType}
                  </FormLabel>
                  <RadioGroup
                    aria-label="issue-type"
                    name="issuetype"
                    className={classes.radioGroup}
                    value={selectedValue}
                    onChange={this.handleRadioChange}
                  >
                    <FormControlLabel
                      className={classes.formControlLabel}
                      classes={{ label: classes.typographyBody1 }}
                      value={FEEDBACK_TEXTS.toolIssue}
                      control={<Radio />}
                      label={FEEDBACK_TEXTS.toolIssue}
                    />
                    <FormControlLabel
                      className={classes.formControlLabel}
                      classes={{ label: classes.typographyBody1 }}
                      value={FEEDBACK_TEXTS.toolQuestion}
                      control={<Radio />}
                      label={FEEDBACK_TEXTS.toolQuestion}
                    />
                    <FormControlLabel
                      className={classes.formControlLabel}
                      classes={{ label: classes.typographyBody1 }}
                      value={FEEDBACK_TEXTS.assetLocation}
                      control={<Radio />}
                      label={FEEDBACK_TEXTS.assetLocation}
                    />
                    <FormControlLabel
                      className={classes.formControlLabel}
                      classes={{ label: classes.typographyBody1 }}
                      value={FEEDBACK_TEXTS.assetRestore}
                      control={<Radio />}
                      label={FEEDBACK_TEXTS.assetRestore}
                    />
                  </RadioGroup>
                </FormControl>
                <Typography variant="h5" className={classes.subTitle}>
                  The <u>{contactTeam}</u> Team will follow up with you.
                </Typography>
                <TextField
                  variant="outlined"
                  id="select-priority"
                  select
                  label={FEEDBACK_TEXTS.responsePriority}
                  className={`${classes.modernTextField} ${classes.compactFormControl}`}
                  value={currency}
                  onChange={this.handlePriorityChange}
                  SelectProps={{
                    native: true,
                    MenuProps: {
                      className: classes.menu,
                    },
                  }}
                  helperText={FEEDBACK_TEXTS.priorityHelper}
                  margin="normal"
                  size="small"
                  InputProps={{
                    style: { fontSize: '0.95rem' },
                  }}
                  InputLabelProps={{
                    style: { fontSize: '0.95rem' },
                  }}
                >
                  {HighPriority.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>

                {/* Asset Management Section - Show only for restore/republish */}
                {selectedValue === 'I need asset restore or republish' && (
                  <Box className={classes.restoreSection}>
                    <Typography
                      variant="subtitle1"
                      className={classes.sectionTitle}
                    >
                      {FEEDBACK_TEXTS.selectAssetType}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      style={{ marginBottom: '16px' }}
                    >
                      <Grid item xs={6}>
                        <Paper
                          className={`${classes.assetTypeCard} ${
                            this.state.assetType === 'DAM' ? 'selected' : ''
                          }`}
                          onClick={() => this.handleAssetTypeSelect('DAM')}
                        >
                          <Box
                            textAlign="center"
                            className={classes.assetTypeCardContent}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="500"
                              style={{ marginBottom: '4px' }}
                            >
                              DAM Assets
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ fontSize: '0.85rem' }}
                            >
                              Internal asset management
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper
                          className={`${classes.assetTypeCard} ${
                            this.state.assetType === 'Scene7' ? 'selected' : ''
                          }`}
                          onClick={() => this.handleAssetTypeSelect('Scene7')}
                        >
                          <Box
                            textAlign="center"
                            className={classes.assetTypeCardContent}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="500"
                              style={{ marginBottom: '4px' }}
                            >
                              Scene7 Assets
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ fontSize: '0.85rem' }}
                            >
                              External publishing platform
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Box
                      className={classes.modernSection}
                      style={{ marginTop: '16px' }}
                    >
                      <Typography
                        variant="subtitle1"
                        className={classes.sectionTitle}
                      >
                        Asset IDs or URLs
                      </Typography>
                      <Box
                        className={`${classes.chipContainer} ${
                          this.state.inputFocused ? 'focused' : ''
                        }`}
                        onClick={() => this.inputRef && this.inputRef.focus()}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {this.state.assets.map((asset, index) => (
                            <Chip
                              key={index}
                              label={asset}
                              onDelete={() => this.removeAsset(asset)}
                              variant="outlined"
                              className={`${classes.assetChip} ${this.getAssetChipClass(asset)}`}
                              deleteIcon={<CloseIcon />}
                            />
                          ))}
                          <input
                            ref={(ref) => (this.inputRef = ref)}
                            className={classes.chipInput}
                            placeholder={
                              this.state.assets.length === 0
                                ? 'Enter asset IDs or URLs (press Enter to add)'
                                : 'Add more assets...'
                            }
                            value={this.state.currentInput}
                            onChange={this.handleAssetInputChange}
                            onKeyPress={this.handleInputKeyPress}
                            onKeyDown={this.handleInputKeyDown}
                            onBlur={() =>
                              this.setState({ inputFocused: false })
                            }
                            onFocus={() =>
                              this.setState({ inputFocused: true })
                            }
                            onPaste={this.handleInputPaste}
                          />
                        </Stack>
                      </Box>
                      <Typography
                        variant="body2"
                        className={classes.assetSummaryText}
                      >
                        {this.state.assets.length}{' '}
                        {this.state.assets.length === 1 ? 'asset' : 'assets'}{' '}
                        added. You can enter multiple IDs separated by commas or
                        new lines.
                      </Typography>
                      {this.state.duplicateAttempt && (
                        <Typography
                          color="error"
                          variant="body2"
                          className={classes.errorText}
                        >
                          Asset "{this.state.duplicateAttempt}" is already added
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <TextField
                  variant="outlined"
                  id="description"
                  label={FEEDBACK_TEXTS.detailedDescription}
                  className={`${classes.modernTextField} ${classes.compactFormControl}`}
                  multiline
                  rows={4}
                  maxRows="8"
                  value={multiline}
                  onChange={this.handleDescriptionChange}
                  margin="normal"
                  helperText={
                    selectedValue === FEEDBACK_TEXTS.assetRestore
                      ? `${FEEDBACK_TEXTS.descriptionHelperOptional} - ${descLength}/2000 characters`
                      : `${FEEDBACK_TEXTS.descriptionHelper} (${descLength}/2000 characters)`
                  }
                  inputProps={{ maxLength: 2000 }}
                  placeholder={
                    selectedValue === FEEDBACK_TEXTS.assetRestore
                      ? FEEDBACK_TEXTS.descriptionPlaceholderOptional
                      : FEEDBACK_TEXTS.descriptionPlaceholder
                  }
                  size="small"
                  InputProps={{
                    style: { fontSize: '0.95rem' },
                  }}
                  InputLabelProps={{
                    style: { fontSize: '0.95rem' },
                  }}
                />
              </form>
            </Grid>
            <Grid
              container
              justifyContent="center"
              spacing={1}
              style={{ padding: '0 18px 0 18px' }}
            >
              <Grid item xs={12}>
                <Box className={classes.modernSection}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    Attach Screenshots (Optional)
                  </Typography>
                  <section>
                    <div className={classes.dropAreaEnhanced}>
                      <Dropzone
                        accept=".jpeg,.png,.jpg"
                        onDrop={this.onDrop.bind(this)}
                      >
                        {({ getRootProps, getInputProps }) => (
                          <section>
                            <div {...getRootProps()}>
                              <input {...getInputProps()} />
                              <div style={{ textAlign: 'center' }}>
                                <CloudUpload
                                  style={{
                                    fontSize: '48px',
                                    color: '#9ca3af',
                                    marginBottom: '8px',
                                  }}
                                />
                                <Typography className={classes.uploadText}>
                                  Drop your screenshots here or click to browse
                                </Typography>
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: '#9ca3af',
                                    display: 'block',
                                    marginBottom: '16px',
                                  }}
                                >
                                  Supports JPG, PNG, JPEG (Max 5MB each)
                                </Typography>
                                <Button
                                  variant="contained"
                                  className={classes.browseButton}
                                >
                                  Browse Files
                                </Button>
                              </div>
                            </div>
                          </section>
                        )}
                      </Dropzone>
                    </div>
                    <aside>
                      <List>
                        {files.map((f, index) => (
                          <ListItem key={index}>
                            <ListItemAvatar>
                              <Avatar>
                                <ImageIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              id="uploadedFile"
                              primary={f.name}
                              secondary={`${f.size} bytes`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </aside>
                  </section>
                </Box>
              </Grid>
            </Grid>
            <div className={classes.root}>
              <div className={classes.wrapper}>
                <Button
                  id="btnFeedBackSubmit"
                  variant="contained"
                  color="secondary"
                  disabled={
                    selectedValue === FEEDBACK_TEXTS.assetRestore
                      ? assetType === '' || assets.length === 0 || loading
                      : descLength < 3 || loading
                  }
                  className={`${classes.button} ${classes.submitButton}`}
                  onClick={this.formSubmit}
                >
                  {FEEDBACK_TEXTS.submit.toUpperCase()}
                  <Send style={{ marginLeft: '8px' }} />
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </div>
          </Grid>
        </ModalDialog>
      </div>
    )
  }
}

Feedback.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string,
  showNotification: PropTypes.func,
  user: PropTypes.object,
  classes: PropTypes.object,
  styles1: PropTypes.object,
}
const mapStateToProps = (state) => ({
  user: {
    email: state.auth.email,
  },
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showNotification,
    },
    dispatch,
  )
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(Feedback))
